<script setup lang="ts">
import {
  Goal,
  GoalItem,
  GoalItemType,
  goalItemTypes,
} from '@/models/goal.model';
import { NEW_ID } from '@/util/constants';
import { PropType, ref, watch } from 'vue';

const props = defineProps({
  items: {
    type: Array as PropType<GoalItem[]>,
    required: true,
  },
  goal: {
    type: Object as PropType<Goal>,
    required: true,
  },
});

const emit = defineEmits(['update']);

const items = ref<GoalItem[]>(
  [...props.items].sort((a, b) => a.column - b.column),
);

// list items
// add item (choose new, or fill out listHeader)

function addItem() {
  items.value = [
    ...items.value,
    {
      id: NEW_ID - items.value.length,
      column: items.value.length,
      type: GoalItemType.header,
      listHeader: '',
    },
  ];

  emit('update', items.value);
}

function update() {
  emit('update', items.value);
}

watch(
  () => props.items,
  () => {
    items.value = [...props.items].sort((a, b) => a.column - b.column);
  },
);

function moveUp(item: GoalItem) {
  const index = items.value.findIndex((a) => a.id === item.id);

  items.value = [
    ...items.value.slice(0, index - 1),
    item,
    items.value[index - 1],
    ...items.value.slice(index + 1),
  ].map((v, index) => ({ ...v, column: index }));

  update();
}

function moveDown(item: GoalItem) {
  const index = items.value.findIndex((a) => a.id === item.id);

  items.value = [
    ...items.value.slice(0, index),
    items.value[index + 1],
    item,
    ...items.value.slice(index + 2),
  ].map((v, index) => ({ ...v, column: index }));

  update();
}

function remove(item: GoalItem) {
  items.value = items.value
    .filter((a) => a.id !== item.id)
    .map((v, index) => ({ ...v, column: index }));

  update();
}
</script>
<template>
  <v-card>
    <v-card-text>
      <v-row v-for="(item, index) in items" :key="item.id">
        <v-col cols="1" align-self="center">{{ index + 1 }}.</v-col>
        <v-col>
          <v-select
            v-model="item.type"
            label="Type"
            density="compact"
            :items="goalItemTypes"
            :hide-details="true"
          />
        </v-col>
        <v-col v-if="item.type === GoalItemType.header">
          <v-text-field
            v-model="item.listHeader"
            label="Header"
            density="compact"
            :list-props="{ maxWidth: '250px' }"
            :hide-details="true"
          />
        </v-col>

        <v-col cols="1" align-self="center">
          <v-btn
            @click="moveUp(item)"
            icon="mdi-arrow-up"
            class="remove-button"
            size="tiny"
            :disabled="index === 0"
          ></v-btn>
          <v-btn
            @click="remove(item)"
            icon="mdi-delete-outline"
            class="remove-button"
            size="tiny"
          ></v-btn>
          <v-btn
            @click="moveDown(item)"
            icon="mdi-arrow-down"
            class="remove-button"
            size="tiny"
            :disabled="index === items.length - 1"
          ></v-btn>
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-text>
      <v-btn type="button" @click="addItem" color="info" flat>Add item</v-btn>
    </v-card-text>
  </v-card>
</template>
